/**
 * Dictionary Generator
 *
 * Runs a minimal EnergyPlus simulation to generate the RDD (Report Data Dictionary)
 * and MDD (Meter Data Dictionary) files. This provides accurate, version-specific
 * output variable and meter names for autocomplete.
 *
 * The approach:
 * 1. Inject Output:VariableDictionary object into the IDF
 * 2. Modify SimulationControl for sizing-only (fast)
 * 3. Run EnergyPlus
 * 4. Parse and return the RDD/MDD content
 */

/**
 * Prepares an IDF for dictionary generation by:
 * 1. Adding Output:VariableDictionary if not present
 * 2. Configuring for sizing-only simulation (faster)
 */
export function prepareIDFForDictionaryGeneration(idfContent: string): string {
  let modifiedIDF = idfContent;

  // Check if Output:VariableDictionary already exists
  const hasVariableDictionary = /Output:VariableDictionary/i.test(idfContent);
  if (!hasVariableDictionary) {
    // Add Output:VariableDictionary to generate RDD file
    modifiedIDF += `

!----- Added for dictionary generation -----
Output:VariableDictionary,IDF;

`;
  }

  // Check if we need to modify SimulationControl for sizing-only
  // This makes the simulation much faster as it skips weather file run periods
  const hasSimulationControl = /SimulationControl,/i.test(idfContent);

  if (hasSimulationControl) {
    // Modify existing SimulationControl to run sizing only
    // SimulationControl has these fields:
    // A1: Do Zone Sizing Calculation
    // A2: Do System Sizing Calculation
    // A3: Do Plant Sizing Calculation
    // A4: Run Simulation for Sizing Periods
    // A5: Run Simulation for Weather File Run Periods
    // A6: Do HVAC Sizing Simulation for Sizing Periods
    // A7: Do HVAC Sizing Simulation for Weather File Run Periods

    // We want: sizing = Yes, weather run = No
    modifiedIDF = modifiedIDF.replace(
      /SimulationControl,([^;]*);/gi,
      (_match, fields) => {
        const fieldArray = fields.split(',').map((f: string) => f.trim());

        // Ensure we have enough fields (pad with empty strings if needed)
        while (fieldArray.length < 7) {
          fieldArray.push('');
        }

        // Set sizing periods to Yes, weather file run periods to No
        fieldArray[3] = 'Yes'; // Run Simulation for Sizing Periods
        fieldArray[4] = 'No'; // Run Simulation for Weather File Run Periods

        return `SimulationControl,${fieldArray.join(',')};`;
      }
    );
  } else {
    // Add SimulationControl for sizing-only
    modifiedIDF += `

!----- Added for dictionary generation -----
SimulationControl,
  Yes,  !- Do Zone Sizing Calculation
  Yes,  !- Do System Sizing Calculation
  Yes,  !- Do Plant Sizing Calculation
  Yes,  !- Run Simulation for Sizing Periods
  No,   !- Run Simulation for Weather File Run Periods
  No,   !- Do HVAC Sizing Simulation for Sizing Periods
  No;   !- Do HVAC Sizing Simulation for Weather File Run Periods

`;
  }

  // Ensure we have at least one SizingPeriod:DesignDay if running sizing
  const hasDesignDay = /SizingPeriod:DesignDay/i.test(idfContent);
  if (!hasDesignDay) {
    // Add a minimal design day so sizing can run
    modifiedIDF += `

!----- Added for dictionary generation -----
SizingPeriod:DesignDay,
  DictionaryGen Summer DD,  !- Name
  7,                        !- Month
  21,                       !- Day of Month
  SummerDesignDay,          !- Day Type
  32,                       !- Maximum Dry-Bulb Temperature {C}
  10,                       !- Daily Dry-Bulb Temperature Range {deltaC}
  ,                         !- Dry-Bulb Temperature Range Modifier Type
  ,                         !- Dry-Bulb Temperature Range Modifier Day Schedule Name
  Wetbulb,                  !- Humidity Condition Type
  20,                       !- Wetbulb or DewPoint at Maximum Dry-Bulb {C}
  ,                         !- Humidity Condition Day Schedule Name
  ,                         !- Humidity Ratio at Maximum Dry-Bulb {kgWater/kgDryAir}
  ,                         !- Enthalpy at Maximum Dry-Bulb {J/kg}
  ,                         !- Daily Wet-Bulb Temperature Range {deltaC}
  101325,                   !- Barometric Pressure {Pa}
  4,                        !- Wind Speed {m/s}
  270,                      !- Wind Direction {deg}
  No,                       !- Rain Indicator
  No,                       !- Snow Indicator
  No,                       !- Daylight Saving Time Indicator
  ASHRAEClearSky,           !- Solar Model Indicator
  ,                         !- Beam Solar Day Schedule Name
  ,                         !- Diffuse Solar Day Schedule Name
  ,                         !- ASHRAE Clear Sky Optical Depth for Beam Irradiance (taub)
  ,                         !- ASHRAE Clear Sky Optical Depth for Diffuse Irradiance (taud)
  1.0;                      !- Sky Clearness

`;
  }

  return modifiedIDF;
}

/**
 * Check if an IDF file is suitable for dictionary generation
 * (has basic required objects)
 */
export function canGenerateDictionary(idfContent: string): {
  canGenerate: boolean;
  missingElements: string[];
} {
  const missingElements: string[] = [];

  // Check for Version
  if (!/Version,/i.test(idfContent)) {
    missingElements.push('Version object');
  }

  // Check for Building
  if (!/Building,/i.test(idfContent)) {
    missingElements.push('Building object');
  }

  // Check for at least one Zone (most variables are zone-related)
  if (!/Zone,/i.test(idfContent)) {
    missingElements.push('At least one Zone object');
  }

  return {
    canGenerate: missingElements.length === 0,
    missingElements,
  };
}

/**
 * Estimate how long dictionary generation will take
 */
export function estimateDictionaryGenerationTime(idfContent: string): {
  estimatedSeconds: number;
  complexity: 'simple' | 'moderate' | 'complex';
} {
  // Count objects to estimate complexity
  const zoneCount = (idfContent.match(/\bZone,/gi) || []).length;
  const surfaceCount = (idfContent.match(/BuildingSurface:Detailed,/gi) || []).length;
  const hvacObjects = (idfContent.match(/\b(Coil:|Fan:|Pump:|Chiller:|Boiler:|AirLoopHVAC)/gi) || []).length;

  const totalComplexity = zoneCount + surfaceCount * 0.1 + hvacObjects * 2;

  if (totalComplexity < 20) {
    return { estimatedSeconds: 5, complexity: 'simple' };
  } else if (totalComplexity < 100) {
    return { estimatedSeconds: 15, complexity: 'moderate' };
  } else {
    return { estimatedSeconds: 30, complexity: 'complex' };
  }
}
